/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * useAuthStore — token/profile persist; phanHe đang mở KHÔNG persist (sessionStorage).
 *
 * Logic: setPhanHe → session + applyPhanHeSliceToProfile; setProfile luôn apply lát PH hiện tại.
 * partialize bỏ phanHe; onRehydrate apply slice từ storage.
 *
 * Actions liên quan PH: setPhanHe, setProfile, clearAuthAll (clearPhanHeFromStorage).
 */
// store/authStore.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ProfileUser } from "@/types/authen";
import { clearTableHiddenColumnsStorage } from '@/states/tableHiddenColumns';
import Cookies from 'js-cookie';
import { applyPhanHeSliceToProfile, getAvailablePhanHeList } from '@/utils/phanHeRoles';
import {
    clearPhanHeFromStorage,
    getPhanHeFromStorage,
    setPhanHeToStorage,
} from '@/utils/phanHeStorage';
import { CONSTANTS } from '@/utils/constants';

const config = {
    prefix: "TOPPION",
    accessToken: "ACCESS_TOKEN",
    refreshToken: "REFRESH_TOKEN",
    tenantID: "TENANT_ID",
} as const;

/** Bộ lọc BSC (FilterBSC) — 3 key riêng, persist cùng bucket auth */
interface AuthState {
    hydrated: boolean;
    accessToken: string | null;
    refreshToken: string | null;
    tenantID: string | null;
    email: string | null;
    profile: ProfileUser | null,
    isAuth: boolean | null,
    bscFilterFrequency: number[] | null;
    bscFilterStatus: number | string | null;
    bscFilterDisplayMode: boolean | null;
    // actions
    setHydrated: (val: boolean) => void;
    setAccessToken: (token: string | null) => void;
    setRefreshToken: (token: string | null) => void;
    setTenantID: (id: string | null) => void;
    setEmail: (email: string | null) => void;
    /** Ghi phân hệ đang mở vào session + apply lát isAdmin/isQuanLy trên profile */
    setPhanHe: (phanHe: string | 'bsc' | 'quytrinh' | 'vanhoa') => void;
    setAuthToken: (user: any) => void;
    setIsAuth: (isLogin: boolean) => void;
    setProfile: (profile: ProfileUser | null) => void;
    setBscFilterFrequency: (value: number[] | null) => void;
    setBscFilterStatus: (value: number | string | null) => void;
    setBscFilterDisplayMode: (value: boolean | null) => void;
    getBscFilterFrequency: () => number[] | null;
    getBscFilterStatus: () => number | string | null;
    getBscFilterDisplayMode: () => boolean | null;
    clearAuthAll: () => Promise<void>;
}

// zustand với persist -> tự động lưu vào localStorage
export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            hydrated: false,
            accessToken: null,
            refreshToken: null,
            tenantID: null,
            email: null,
            profile: null,
            isAuth: null,
            bscFilterFrequency: null,
            bscFilterStatus: null,
            bscFilterDisplayMode: null,
            setHydrated: (val) => set({ hydrated: val }),
            setAccessToken: (token) => set({ accessToken: token }),
            setRefreshToken: (token) => set({ refreshToken: token }),
            setTenantID: (id) => set({ tenantID: id }),
            setEmail: (email) => set({ email }),
            setPhanHe: (phanHe) => {
                setPhanHeToStorage(phanHe);
                const profile = get().profile;
                if (!profile) return;
                set({ profile: applyPhanHeSliceToProfile(profile, phanHe) });
            },
            setAuthToken: (user: any) => set({
                accessToken: user.accessToken,
                refreshToken: user.refreshToken,
                tenantID: user.tenantID,
                // Không gán phanHe từ login payload (danh sách PH user ≠ PH đang mở)
            }),
            setIsAuth: (isLogin: boolean) =>
                set((state) => {
                    if (isLogin && state.accessToken) {
                        Cookies.set("accessToken", state.accessToken, { expires: 14, path: "/" });
                    } else {
                        Cookies.remove("accessToken");
                    }
                    return { isAuth: isLogin };
                }),
            setProfile: (profile) => {
                if (!profile) {
                    set({ profile: null });
                    return;
                }
                const activePhanHe =
                    getPhanHeFromStorage() || CONSTANTS.PHAN_HE.BSC;
                const availablePhanHe = getAvailablePhanHeList(
                    profile,
                    get().profile?.availablePhanHe,
                );
                set({
                    profile: applyPhanHeSliceToProfile(
                        { ...profile, availablePhanHe },
                        activePhanHe,
                    ),
                });
            },
            setBscFilterFrequency: (value) => set({ bscFilterFrequency: value }),
            setBscFilterStatus: (value) => set({ bscFilterStatus: value }),
            setBscFilterDisplayMode: (value) => set({ bscFilterDisplayMode: value }),
            getBscFilterFrequency: () => get().bscFilterFrequency,
            getBscFilterStatus: () => get().bscFilterStatus,
            getBscFilterDisplayMode: () => get().bscFilterDisplayMode,
            clearAuthAll: async () => {
                // Clear menu state from sessionStorage
                try {
                    sessionStorage.removeItem('menu-state');
                } catch (error) {
                    console.error('Lỗi khi xóa menu-state khỏi sessionStorage:', error);
                }

                // Clear cached "Giá trị cốt lõi" used by Văn hoá pages
                try {
                    localStorage.removeItem('giatriCotLoiList');
                } catch (error) {
                    console.error('Lỗi khi xóa giatriCotLoiList khỏi localStorage:', error);
                }

                try {
                    clearPhanHeFromStorage();
                } catch (error) {
                    console.error('Lỗi khi xóa phanHe khỏi storage:', error);
                }

                try {
                    clearTableHiddenColumnsStorage();
                } catch (error) {
                    console.error('Lỗi khi xóa prefs cột bảng khỏi localStorage:', error);
                }

                set({
                    accessToken: null,
                    refreshToken: null,
                    tenantID: null,
                    profile: null,
                    isAuth: null,
                    bscFilterFrequency: [3, 4, 5, 2],
                    bscFilterStatus: 1,
                    bscFilterDisplayMode: null,
                });
                Cookies.remove("accessToken");
            },
        }),
        {
            name: `${config.prefix}:auth`, // key lưu vào localStorage
            partialize: (state) => ({
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                tenantID: state.tenantID,
                email: state.email,
                profile: state.profile,
                isAuth: state.isAuth,
                bscFilterFrequency: state.bscFilterFrequency,
                bscFilterStatus: state.bscFilterStatus,
                bscFilterDisplayMode: state.bscFilterDisplayMode,
                // không persist phanHe — chỉ sống trong sessionStorage
            }),
            // Legacy sessions may contain a derived BSC-only profile. Obtain a
            // fresh login once; never infer or broaden access from cached roles.
            version: 1,
            migrate: (persistedState) => {
                const previous = (persistedState ?? {}) as Partial<AuthState>;
                Cookies.remove("accessToken", { path: "/" });
                try {
                    localStorage.removeItem("phanHe");
                    sessionStorage.removeItem("phanHe");
                    sessionStorage.removeItem("menu-state");
                } catch {
                    // Restricted storage must not prevent invalidating auth.
                }
                return {
                    ...previous,
                    email: previous.email ?? null,
                    bscFilterFrequency: previous.bscFilterFrequency ?? null,
                    bscFilterStatus: previous.bscFilterStatus ?? null,
                    bscFilterDisplayMode: previous.bscFilterDisplayMode ?? null,
                    accessToken: null,
                    refreshToken: null,
                    tenantID: null,
                    profile: null,
                    isAuth: false,
                    phanHe: "bsc",
                };
            },
            merge: (persistedState, currentState) => {
                const p = (persistedState ?? {}) as Record<string, unknown>;
                const merged: Record<string, unknown> = { ...currentState, ...p };
                // bỏ legacy phanHe trong bucket auth cũ
                delete merged.phanHe;

                const legacy = merged.bscFilterState as
                    | { frequency?: number[]; status?: number | string; displayMode?: boolean }
                    | undefined;
                if (legacy != null && typeof legacy === "object" && !Array.isArray(legacy)) {
                    merged.bscFilterFrequency = legacy.frequency ?? merged.bscFilterFrequency ?? null;
                    merged.bscFilterStatus = legacy.status ?? merged.bscFilterStatus ?? null;
                    merged.bscFilterDisplayMode = legacy.displayMode ?? merged.bscFilterDisplayMode ?? null;
                }
                delete merged.bscFilterState;

                if (merged.bscFilterFrequency === undefined) merged.bscFilterFrequency = [3, 4, 5, 2];
                if (merged.bscFilterStatus === undefined) merged.bscFilterStatus = 1;
                if (merged.bscFilterDisplayMode === undefined) merged.bscFilterDisplayMode = null;

                return merged as unknown as AuthState;
            },
            onRehydrateStorage: () => (state) => {
                if (state?.profile) {
                    const activePhanHe = getPhanHeFromStorage() || CONSTANTS.PHAN_HE.BSC;
                    state.setProfile(applyPhanHeSliceToProfile(state.profile, activePhanHe));
                }
                state?.setHydrated(true);
            },
        }
    )
);

export const getBscFilterFrequency = (): number[] | null =>
    useAuthStore.getState().getBscFilterFrequency();

export const getBscFilterStatus = (): number | string | null =>
    useAuthStore.getState().getBscFilterStatus();

export const getBscFilterDisplayMode = (): boolean | null =>
    useAuthStore.getState().getBscFilterDisplayMode();
